const colours = {
  white: '#ffffff',

  whiteDark1: '#f7f7f7',
  greyLight9: '#f2f2f2',
  greyLight8: '#ebebeb',
  greyLight7: '#e8e8e8',
  greyLight6: '#dddddd',
  greyLight5: '#cccccc',
  greyLight4: '#b6b6b6',
  greyLight3: '#aaaaaa',
  greyLight2: '#999999',
  greyLight1: '#777777',
  grey: '#666666',
  greyDark1: '#555555',
  greyDark2: '#434343',
  greyDark3: '#333333',

  black: '#111111',
  blackDark1: '#000000',

  slateLight3: '#929297',
  slateLight2: '#6b7280',
  slateLight1: '#454550',
  slate: '#252530',
  slateDark1: '#112233',

  greenLight9: '#e6fff2',
  greenLight8: '#e7f5e6',
  greenLight7: '#2ee68c',
  greenLight6: '#a7d4a7',
  greenLight5: '#81c784',
  greenLight4: '#32cd32',
  greenLight3: '#57aa57',
  greenLight2: '#319731',
  greenLight1: '#009300',
  green: '#007f00',
  greenDark1: '#007833',
  greenDark2: '#226623',
  greenDark3: '#006420',
  greenDark4: '#0f5b13',
  greenDark5: '#005921',
  greenDark6: '#115217',
  greenDark7: '#004f00',
  greenDark8: '#004306',

  limeLight4: '#f7f8f0',
  limeLight3: '#f1f6e2',
  limeLight2: '#cce57f',
  limeLight1: '#bed68f',
  lime: '#99cc00',

  blueLight9: '#ecfafe',
  blueLight8: '#94dcf8',
  blueLight7: '#4fc3f7',
  blueLight6: '#00bfff',
  blueLight5: '#5bbad5',
  blueLight4: '#00b7ff',
  blueLight3: '#0094ff',
  blueLight2: '#007ac9',
  blueLight1: '#0000ff',
  blue: '#000099',
  blueDark1: '#000033',

  purpleLight1: '#f2ceef',
  purple: '#8884d8',

  cyan: '#0ff0ff',

  redLight8: '#ffedf0',
  redLight7: '#fbeae5',
  redLight6: '#f8d7da',
  redLight5: '#ff7591',
  redLight4: '#f66f66',
  redLight3: '#f06292',
  redLight2: '#e64949',
  redLight1: '#ff0000',
  red: '#d24614',
  redDark1: '#cc0000',

  orangeLight2: '#fdece3',
  orangeLight1: '#ffb74d',
  orange: '#ffa500',
  orangeDark1: '#edac1a',
  orangeDark2: '#ff8c00',
  orangeDark3: '#ff8831',
  orangeDark4: '#ff6600',

  yellowLight3: '#ffff00',
  yellowLight2: '#fff3cd',
  yellowLight1: '#fff176',
  yellow: '#ffee5c',
  yellowDark1: '#ffe600',
  yellowDark2: '#f8cd47',

  olive: '#494736',

  transparent: 'rgba(0, 0, 0, 0)',
  shadowLight2: 'rgba(17, 17, 17, 0.05)',
  shadowLight1: 'rgba(17, 17, 17, 0.07)',
  shadow: 'rgba(17, 17, 17, 0.1)',
  shadowDark1: 'rgba(0, 0, 0, 0.11)',
  shadowDark2: 'rgba(0, 0, 0, 0.14)',
  shadowDark3: 'rgba(0, 0, 0, 0.2)',
  shadowDark4: 'rgba(35, 35, 35, 0.24)',
  shadowDark5: 'rgba(0, 0, 0, 0.5)',
  shadowDark6: 'rgba(17, 17, 17, 0.64)',
  shadowDark7: 'rgba(0, 0, 0, 0.7)',
  shadowDark8: 'rgba(0, 0, 0, 1)',

  whiteOverlay: 'rgba(255, 255, 255, 0.8)',
  whiteOverlayDark1: 'rgba(255, 255, 255, 0.7)',
  whiteOverlayDark2: 'rgba(255, 255, 255, 0.3)',
  whiteOverlayDark3: 'rgba(255, 255, 255, 0.1)',

  slateOverlay: 'rgba(37, 37, 48, 0.5)',
  greenOverlay: 'rgba(0, 127, 0, 0.2)',
  greenOverlayDark1: 'rgba(0, 100, 0, 0.2)',
  redOverlay: 'rgba(255, 0, 0, 0.2)',
  redOverlayDark1: 'rgba(210, 71, 20, 0.75)',
} as const;

export type ColourName = keyof typeof colours;
export type ColourTheme = { [colour in ColourName]: string };

export function isColourName(colour: string): colour is ColourName {
  return colour in colours;
}

export const colourNames = Object.keys(colours).filter(isColourName);
export const colourTheme: ColourTheme = colours;

export function getColourVariable(colour: ColourName): string {
  return `var(--colour-${colour.replace(/([A-Z])/g, '-$1').toLowerCase()})`;
}

export function getColourVariables(theme: { Colours?: ColourTheme } | undefined): string {
  return colourNames
    .map(
      (colour) =>
        `--colour-${colour.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${theme?.Colours?.[colour] ?? colours[colour]};`,
    )
    .join('\n');
}

export const scopeColours = {
  'scope-1': getColourVariable('green'),
  'scope-2': getColourVariable('lime'),
  'scope-3': getColourVariable('greenDark8'),
};

const colourVariables = Object.fromEntries(
  colourNames.map((colour) => [colour, getColourVariable(colour)]),
) as ColourTheme;

export default colourVariables;
